const CF_ENDPOINT = process.env.REACT_APP_FRAPPE_LEAD_ENDPOINT || '';

/**
 * Configuration for retry logic
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
};

/**
 * Delay utility with exponential backoff
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateBackoffDelay(attempt) {
  const exponentialDelay = RETRY_CONFIG.baseDelayMs * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
  const jitter = Math.random() * 500; // Add 0-500ms random jitter
  return Math.min(exponentialDelay + jitter, RETRY_CONFIG.maxDelayMs);
}

/**
 * Check if error is retryable (network errors, 5xx, rate limits)
 */
function isRetryableError(error, response) {
  // Network errors are retryable
  if (!response) return true;
  
  // 5xx server errors are retryable
  if (response.status >= 500) return true;
  
  // 429 rate limit is retryable
  if (response.status === 429) return true;
  
  // 408 request timeout is retryable
  if (response.status === 408) return true;
  
  return false;
}

/**
 * Submit lead to Frappe with robust retry logic
 * @param {Object} payload - Lead data
 * @param {Object} options - Submission options
 * @param {number} options.maxRetries - Override max retries
 * @param {boolean} options.throwOnFail - Whether to throw on final failure (default: true)
 * @returns {Promise<Object>} - Response data with success flag
 */
export async function submitConsultationLead(payload, options = {}) {
  const { maxRetries = RETRY_CONFIG.maxRetries, throwOnFail = true } = options;
  
  if (!CF_ENDPOINT) {
    const error = new Error('Missing REACT_APP_FRAPPE_LEAD_ENDPOINT. Please set it in your environment.');
    console.error('[FrappeLeadService] Configuration error:', error.message);
    if (throwOnFail) throw error;
    return { success: false, error: error.message, retryable: false };
  }

  const required = ['first_name', 'last_name', 'email', 'mobile_no'];
  for (const key of required) {
    if (!payload?.[key] || String(payload[key]).trim().length === 0) {
      const error = new Error(`Missing required field: ${key}`);
      console.error('[FrappeLeadService] Validation error:', error.message);
      if (throwOnFail) throw error;
      return { success: false, error: error.message, retryable: false };
    }
  }

  const body = {
    first_name: payload.first_name,
    last_name: payload.last_name,
    email: payload.email,
    mobile_no: payload.mobile_no,
    notes: payload.notes || 'Lead from wizard — website',
    custom_countryselection: payload.custom_countryselection || payload.country,
    custom_personal_info: typeof payload.custom_personal_info === 'object'
      ? JSON.stringify(payload.custom_personal_info)
      : payload.custom_personal_info,
    custom_education: typeof payload.custom_education === 'object'
      ? JSON.stringify(payload.custom_education)
      : payload.custom_education,
    custom_experience: typeof payload.custom_experience === 'object'
      ? JSON.stringify(payload.custom_experience)
      : payload.custom_experience,
    custom_language_skills: typeof payload.custom_language_skills === 'object'
      ? JSON.stringify(payload.custom_language_skills)
      : payload.custom_language_skills,
    custom_financial_info: typeof payload.custom_financial_info === 'object'
      ? JSON.stringify(payload.custom_financial_info)
      : payload.custom_financial_info,
    custom_selected_pathway: payload.custom_selected_pathway,
    custom_selected_package: payload.custom_selected_package,
    custom_scoring_results: typeof payload.custom_scoring_results === 'object'
      ? JSON.stringify(payload.custom_scoring_results)
      : payload.custom_scoring_results,
    custom_upload_cv: typeof payload.custom_upload_cv === 'object'
      ? JSON.stringify(payload.custom_upload_cv)
      : payload.custom_upload_cv,
    custom_tab_7: payload.custom_tab_7,
  };

  let lastError = null;
  let lastResponse = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[FrappeLeadService] Attempt ${attempt + 1}/${maxRetries + 1} - Submitting lead for ${payload.email}`);
      console.log(`[FrappeLeadService] Sending to endpoint: ${CF_ENDPOINT}`);

      const resp = await fetch(CF_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      console.log(`[FrappeLeadService] Response status: ${resp.status}, ok: ${resp.ok}`);

      lastResponse = resp;
      const contentType = resp.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await resp.json() : await resp.text();

      if (resp.ok) {
        console.log(`[FrappeLeadService] SUCCESS - Lead created for ${payload.email}`, data);
        return {
          success: true,
          data,
          attempts: attempt + 1,
          leadId: data?.data?.name || data?.name || null
        };
      }

      // Not OK - extract error message
      const msg = typeof data === 'string' ? data : data?.message || data?.error || 'Failed to submit lead';
      lastError = new Error(msg);
      lastError.status = resp.status;
      lastError.response = data;

      // Check if retryable
      if (!isRetryableError(lastError, resp)) {
        console.error(`[FrappeLeadService] Non-retryable error (${resp.status}):`, msg);
        if (throwOnFail) throw lastError;
        return { success: false, error: msg, retryable: false, status: resp.status };
      }

      // Log and retry
      if (attempt < maxRetries) {
        const delayMs = calculateBackoffDelay(attempt);
        console.warn(`[FrappeLeadService] Retryable error (${resp.status}), retrying in ${Math.round(delayMs)}ms...`, msg);
        await delay(delayMs);
      }

    } catch (error) {
      lastError = error;

      // Log detailed error information for debugging
      console.error(`[FrappeLeadService] Fetch error details:`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
        endpoint: CF_ENDPOINT,
        attempt: attempt + 1
      });

      // Network error - retryable
      if (attempt < maxRetries) {
        const delayMs = calculateBackoffDelay(attempt);
        console.warn(`[FrappeLeadService] Network error, retrying in ${Math.round(delayMs)}ms...`, error.message);
        await delay(delayMs);
      }
    }
  }

  // All retries exhausted
  const errorMsg = lastError?.message || 'Failed to submit lead after all retries';
  console.error(`[FrappeLeadService] FAILED after ${maxRetries + 1} attempts for ${payload.email}:`, errorMsg);
  
  if (throwOnFail) throw lastError || new Error(errorMsg);
  return {
    success: false,
    error: errorMsg,
    retryable: true,
    attempts: maxRetries + 1,
    status: lastResponse?.status
  };
}

/**
 * Create an initial Frappe lead from CV upload data
 * Called immediately when CV is analyzed, before wizard completion
 * @param {Object} cvData - CV data from analysis
 * @param {Object} wizardState - Current wizard state (partial)
 * @param {Object} options - Submission options
 * @returns {Promise<Object>} - Result with success flag and lead ID
 */
export async function createInitialLeadFromCV(cvData, wizardState, options = {}) {
  const { maxRetries = 2, throwOnFail = false } = options;

  if (!CF_ENDPOINT) {
    console.error('[FrappeLeadService] Configuration error: Missing endpoint');
    if (throwOnFail) throw new Error('Missing REACT_APP_FRAPPE_LEAD_ENDPOINT');
    return { success: false, error: 'Configuration error', retryable: false };
  }

  // Extract available data from CV and wizard state
  const personalInfo = wizardState.personalInfo || {};
  const countrySelection = wizardState.countrySelection || {};

  // CV data structure is flat - contains personalInfo, education, experience, etc. directly
  // NOT nested under extractedData
  const cvPersonalInfo = cvData?.personalInfo || {};

  // Build partial payload - only what we know at CV upload time
  const payload = {
    // Required fields - try to get from CV or wizard
    // Use fallback values if not available to avoid validation errors
    first_name: personalInfo.firstName || cvPersonalInfo.firstName || personalInfo.name || 'CV Upload',
    last_name: personalInfo.lastName || cvPersonalInfo.lastName || personalInfo.surname || 'Candidate',
    email: personalInfo.email || cvPersonalInfo.email || 'noreply@cventrytemp.com',
    mobile_no: personalInfo.phone || cvPersonalInfo.phone || cvPersonalInfo.mobile || '+0000000000',

    // Note: Don't send source/lead_type as they don't exist in Frappe
    notes: 'Initial lead created from CV upload. Wizard not yet completed.',

    // Country selection (if available)
    country: countrySelection.selectedCountry || '',
    custom_countryselection: countrySelection.selectedCountry || '',

    // CV data (this is the main data we have)
    custom_upload_cv: {
      url: cvData.url || cvData.publicUrl,
      fileName: cvData.fileName,
      fileSize: cvData.fileSize,
      fileType: cvData.fileType,
      storagePath: cvData.storagePath || cvData.path,
      aiAnalysis: cvData.aiAnalysis,
      uploadedAt: cvData.uploadedAt,
    },

    // Personal info from CV extraction
    custom_personal_info: {
      firstName: personalInfo.firstName || cvPersonalInfo.firstName,
      lastName: personalInfo.lastName || cvPersonalInfo.lastName,
      email: personalInfo.email || cvPersonalInfo.email,
      phone: personalInfo.phone || cvPersonalInfo.phone,
      dateOfBirth: personalInfo.dateOfBirth || cvPersonalInfo.dateOfBirth,
      nationality: personalInfo.nationality || cvPersonalInfo.nationality,
      currentCountry: personalInfo.currentCountry || cvPersonalInfo.currentCountry,
      // CV extracted details
      cvExtractedAt: cvData.uploadedAt,
    },

    // Education from CV (if extracted)
    custom_education: cvData?.education ? {
      highestEducation: cvData.education.highestEducation || cvData.education.level,
      fieldOfStudy: cvData.education.fieldOfStudy,
      graduationYear: cvData.education.graduationYear,
      institution: cvData.education.institution,
      source: 'CV Analysis'
    } : null,

    // Experience from CV (if extracted)
    custom_experience: cvData?.experience ?
      (Array.isArray(cvData.experience) ? cvData.experience : [cvData.experience])
      : null,

    // Language skills from CV (if extracted)
    custom_language_skills: cvData?.languageProficiency ? {
      englishSpeaking: cvData.languageProficiency.english,
      englishWriting: cvData.languageProficiency.english,
      germanLevel: cvData.languageProficiency.german,
    } : null,

    // Tab 7 data (if available from wizard state)
    custom_tab_7: wizardState.custom_tab_7 || null,
  };

  // Use the existing submitConsultationLead with custom options
  try {
    console.log('[FrappeLeadService] Creating initial lead from CV upload...');
    console.log('[FrappeLeadService] Payload data:', {
      first_name: payload.first_name,
      last_name: payload.last_name,
      email: payload.email,
      mobile_no: payload.mobile_no,
      has_country: !!payload.custom_countryselection,
      has_cv_data: !!payload.custom_upload_cv,
      endpoint: CF_ENDPOINT
    });
    const result = await submitConsultationLead(payload, { maxRetries, throwOnFail });
    console.log('[FrappeLeadService] Initial lead creation result:', result);
    return result;
  } catch (error) {
    console.error('[FrappeLeadService] Failed to create initial lead:', error.message);
    console.error('[FrappeLeadService] Error details:', error);
    return { success: false, error: error.message, retryable: true };
  }
}

/**
 * Update an existing Frappe lead with complete wizard data
 * @param {string} leadId - The Frappe lead ID to update
 * @param {Object} wizardData - Complete wizard state
 * @param {Object} options - Update options
 * @returns {Promise<Object>} - Result with success flag
 */
export async function updateFrappeLead(leadId, wizardData, options = {}) {
  const { maxRetries = RETRY_CONFIG.maxRetries, throwOnFail = true } = options;

  if (!CF_ENDPOINT) {
    const error = new Error('Missing REACT_APP_FRAPPE_LEAD_ENDPOINT');
    console.error('[FrappeLeadService] Configuration error:', error.message);
    if (throwOnFail) throw error;
    return { success: false, error: error.message, retryable: false };
  }

  if (!leadId) {
    const error = new Error('No Frappe lead ID provided for update');
    console.error('[FrappeLeadService] Missing lead ID');
    if (throwOnFail) throw error;
    return { success: false, error: error.message, retryable: false };
  }

  // Map wizard data to Frappe update format
  const payload = mapWizardDataToFrappePayload(wizardData);

  // Build update body (only include fields that have values)
  const updateBody = {
    first_name: payload.first_name,
    last_name: payload.last_name,
    email: payload.email,
    mobile_no: payload.mobile_no,
    notes: (payload.notes || '') + ' (Updated after wizard completion)',
    country: payload.country,
    custom_countryselection: payload.custom_countryselection,
    custom_personal_info: payload.custom_personal_info,
    custom_education: payload.custom_education,
    custom_experience: payload.custom_experience,
    custom_language_skills: payload.custom_language_skills,
    custom_financial_info: payload.custom_financial_info,
    custom_selected_pathway: payload.custom_selected_pathway,
    custom_selected_package: payload.custom_selected_package,
    custom_scoring_results: payload.custom_scoring_results,
    // Merge CV data if not already present
    custom_upload_cv: payload.custom_upload_cv,
    custom_tab_7: payload.custom_tab_7,
  };

  let lastError = null;
  let lastResponse = null;

  // Retry loop for update
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[FrappeLeadService] Update attempt ${attempt + 1}/${maxRetries + 1} for lead ${leadId}`);

      // NOTE: This requires the Cloudflare Worker to support PUT/PATCH
      const resp = await fetch(`${CF_ENDPOINT}/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateBody),
      });

      lastResponse = resp;
      const contentType = resp.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await resp.json() : await resp.text();

      if (resp.ok) {
        console.log(`[FrappeLeadService] SUCCESS - Lead updated: ${leadId}`);
        return {
          success: true,
          data,
          attempts: attempt + 1,
          leadId: leadId
        };
      }

      const msg = typeof data === 'string' ? data : data?.message || data?.error || 'Failed to update lead';
      lastError = new Error(msg);
      lastError.status = resp.status;
      lastError.response = data;

      if (!isRetryableError(lastError, resp)) {
        console.error(`[FrappeLeadService] Non-retryable error (${resp.status}):`, msg);
        if (throwOnFail) throw lastError;
        return { success: false, error: msg, retryable: false, status: resp.status };
      }

      if (attempt < maxRetries) {
        const delayMs = calculateBackoffDelay(attempt);
        console.warn(`[FrappeLeadService] Retryable error, retrying in ${Math.round(delayMs)}ms...`);
        await delay(delayMs);
      }

    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        const delayMs = calculateBackoffDelay(attempt);
        console.warn(`[FrappeLeadService] Network error on update, retrying in ${Math.round(delayMs)}ms...`);
        await delay(delayMs);
      }
    }
  }

  const errorMsg = lastError?.message || 'Failed to update lead after all retries';
  console.error(`[FrappeLeadService] FAILED to update lead ${leadId} after ${maxRetries + 1} attempts`);

  if (throwOnFail) throw lastError || new Error(errorMsg);
  return {
    success: false,
    error: errorMsg,
    retryable: true,
    attempts: maxRetries + 1,
    status: lastResponse?.status
  };
}

/**
 * Map wizard submission data to Frappe lead payload format
 * @param {Object} wizardData - Complete wizard state data
 * @returns {Object} - Frappe-compatible payload
 */
export function mapWizardDataToFrappePayload(wizardData) {
  const personalInfo = wizardData.personalInfo || {};
  const countrySelection = wizardData.countrySelection || {};
  const education = wizardData.education || {};
  const experience = wizardData.experience || [];
  const languageSkills = wizardData.languageSkills || {};
  const financialInfo = wizardData.financialInfo || {};
  const cvData = wizardData.cvData || null;
  const reviewData = wizardData.reviewData || {};
  const scores = wizardData.scores || wizardData.scoringResults || {};

  return {
    // Required fields
    first_name: personalInfo.firstName || '',
    last_name: personalInfo.lastName || '',
    email: personalInfo.email || '',
    mobile_no: personalInfo.phone || '',
    
    // Source tracking
    source: 'Website',
    lead_type: 'Wizard',
    notes: `Lead from wizard submission. Contact preference: ${wizardData.contactPreference || 'email'}`,
    
    // Country selection
    country: countrySelection.selectedCountry || '',
    custom_countryselection: countrySelection.selectedCountry || '',
    
    // Personal info (as JSON for custom field)
    custom_personal_info: {
      firstName: personalInfo.firstName,
      lastName: personalInfo.lastName,
      email: personalInfo.email,
      phone: personalInfo.phone,
      dateOfBirth: personalInfo.dateOfBirth,
      nationality: personalInfo.nationality,
      currentCountry: personalInfo.currentCountry,
    },
    
    // Education info
    custom_education: {
      highestEducation: education.highestEducation,
      fieldOfStudy: education.fieldOfStudy,
      graduationYear: education.graduationYear,
      institution: education.institution,
      gpa: education.gpa,
    },
    
    // Experience info (array)
    custom_experience: Array.isArray(experience) && experience.length > 0
      ? experience.map(exp => ({
          totalExperience: exp.totalExperience,
          jobTitle: exp.jobTitle,
          company: exp.company,
          industry: exp.industry,
          responsibilities: exp.responsibilities,
        }))
      : null,
    
    // Language skills
    custom_language_skills: {
      englishSpeaking: languageSkills.englishSpeaking,
      englishWriting: languageSkills.englishWriting,
      germanLevel: languageSkills.germanLevel,
      hasIELTS: languageSkills.hasIELTS,
      ieltsScore: languageSkills.ieltsScore,
      otherLanguages: languageSkills.otherLanguages,
    },
    
    // Financial info (excluding budget as per user request)
    custom_financial_info: {
      // Note: educationBudget intentionally excluded from Frappe sync
      sponsorType: financialInfo.sponsorType,
      financialProof: financialInfo.financialProof,
    },
    
    // Pathway and package selection
    custom_selected_pathway: reviewData.selectedPathway || wizardData.selectedPathway || '',
    custom_selected_package: reviewData.selectedPackage || wizardData.selectedPackage || '',
    
    // Scoring results
    custom_scoring_results: scores,
    
    // CV data
    custom_upload_cv: cvData ? {
      url: cvData.url || cvData.publicUrl,
      fileName: cvData.fileName,
      fileSize: cvData.fileSize,
      fileType: cvData.fileType,
      storagePath: cvData.path || cvData.storagePath,
      aiAnalysis: cvData.aiAnalysis,
    } : null,
  };
}

/**
 * Submit wizard data to Frappe with full mapping and retry logic
 * This is the main function to call from WizardSubmissionService
 * @param {Object} wizardData - Complete wizard state
 * @param {Object} options - Submission options
 * @returns {Promise<Object>} - Result with success flag and lead details
 */
export async function submitWizardLead(wizardData, options = {}) {
  console.log('[FrappeLeadService] Mapping wizard data to Frappe payload...');
  const payload = mapWizardDataToFrappePayload(wizardData);
  
  console.log('[FrappeLeadService] Submitting wizard lead...', {
    email: payload.email,
    pathway: payload.custom_selected_pathway
  });
  
  return submitConsultationLead(payload, options);
}
